import { generateHTML, generateJSON, type Extensions } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { CodeBlockLowlight } from '@tiptap/extension-code-block-lowlight';
import { createLowlight, common } from 'lowlight';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Link } from '@tiptap/extension-link';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';

const lowlight = createLowlight(common);

/**
 * Extensions mirroring the always-on subset of `RichTextEditor`'s own
 * `useEditorExtensions` (StarterKit/Table/Link/TaskList/marks/CodeBlockLowlight).
 * Decoration-only extensions (Placeholder, drag handle, slash commands) and the
 * optionally-gated ones we disable for notes (images, video, mentions, callouts,
 * expandable) are intentionally omitted — they don't affect the HTML/JSON schema
 * for the marks and nodes notes actually use.
 *
 * @remarks
 * Kept in sync manually with `shared/components/custom/rich-text-editor/hooks/useEditorExtensions.ts`
 * because that hook is React-only and not reusable outside a component.
 */
const CONTENT_EXTENSIONS: Extensions = [
  StarterKit.configure({ codeBlock: false }),
  CodeBlockLowlight.configure({ lowlight }),
  Table.configure({ resizable: true }),
  TableRow,
  TableCell,
  TableHeader,
  Link.configure({
    openOnClick: false,
    HTMLAttributes: { rel: 'noopener noreferrer', target: null },
  }),
  TaskList,
  TaskItem.configure({ nested: true }),
  Underline,
  TextStyle,
  Color,
  Highlight.configure({ multicolor: true }),
  Subscript,
  Superscript,
];

/** Converts sanitized HTML from the Notes API into the TipTap JSON string `RichTextEditor` expects as `value`. */
export function htmlToEditorJson(html: string): string {
  if (!html.trim()) return JSON.stringify({ type: 'doc', content: [{ type: 'paragraph' }] });
  return JSON.stringify(generateJSON(html, CONTENT_EXTENSIONS));
}

/** Converts the TipTap JSON string from `RichTextEditor`'s `onChange` into HTML for the Notes API. */
export function editorJsonToHtml(json: string): string {
  const parsed = JSON.parse(json) as object;
  return generateHTML(parsed, CONTENT_EXTENSIONS);
}
